"use client";

import { Input } from "@/components/base/input";
import { cn } from "@/lib/utils";
import * as React from "react";
import { z } from "zod";

const publicLocationsInsertSchema = z.object({
  addr1: z.string().optional().nullable(),
  addr2: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  created_at: z.string().optional(),
  id: z.string().optional(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
  place_id: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  text: z.string().optional().nullable(),
  updated_at: z.string().optional(),
  zip: z.string().optional().nullable(),
});

type PublicLocationsInsert = z.infer<typeof publicLocationsInsertSchema>;

interface PlacePrediction {
  placeId: string;
  text: {
    text: string;
    matches?: Array<{
      startOffset?: number;
      endOffset: number;
    }>;
  };
  structuredFormat?: {
    mainText: {
      text: string;
      matches?: Array<{
        startOffset?: number;
        endOffset: number;
      }>;
    };
    secondaryText?: {
      text: string;
    };
  };
  types?: string[];
  distanceMeters?: number;
}

interface GooglePlacesAutocompleteProps
  extends Omit<React.ComponentProps<"input">, "onChange" | "value"> {
  value?: string;
  onChange?: (value: string) => void;
  onPlaceSelect?: (place: PlacePrediction) => void;
  onLocationSelect?: (location: PublicLocationsInsert) => void;
  locationBias?: {
    circle: {
      center: {
        latitude: number;
        longitude: number;
      };
      radius: number;
    };
  };
  locationRestriction?: {
    circle: {
      center: {
        latitude: number;
        longitude: number;
      };
      radius: number;
    };
  };
  includedPrimaryTypes?: string[];
  includedRegionCodes?: string[];
  origin?: {
    latitude: number;
    longitude: number;
  };
  region?: string;
  sessionToken?: string;
  apiKey?: string;
}

export function GooglePlacesAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  onLocationSelect,
  locationBias,
  locationRestriction,
  includedPrimaryTypes,
  includedRegionCodes,
  origin,
  region,
  sessionToken,
  apiKey,
  className,
  ...props
}: GooglePlacesAutocompleteProps) {
  const [inputValue, setInputValue] = React.useState(value || "");
  const [predictions, setPredictions] = React.useState<PlacePrediction[]>([]);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = React.useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  // Update internal state when value prop changes
  React.useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchPredictions = React.useCallback(
    async (input: string) => {
      if (!input.trim() || !apiKey) {
        setPredictions([]);
        return;
      }

      try {
        const response = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": apiKey,
          },
          body: JSON.stringify({
            input,
            locationBias,
            locationRestriction,
            includedPrimaryTypes,
            includedRegionCodes,
            origin,
            region,
            sessionToken,
            apiKey,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch predictions");
        }

        const data = await response.json();
        setPredictions(data.predictions || []);
        setShowDropdown(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Error fetching place predictions:", error);
        setPredictions([]);
      }
    },
    [
      locationBias,
      locationRestriction,
      includedPrimaryTypes,
      includedRegionCodes,
      origin,
      region,
      sessionToken,
      apiKey,
    ]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);

    // Debounce the API call
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, 300) as ReturnType<typeof setTimeout>;
  };

  const handleSelectPrediction = async (prediction: PlacePrediction) => {
    setInputValue(prediction.text.text);
    onChange?.(prediction.text.text);
    onPlaceSelect?.(prediction);
    
    // Fetch detailed place information using the Place ID
    try {
      const response = await fetch("/api/places/details", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          placeId: prediction.placeId,
          sessionToken,
          apiKey,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch place details");
      }

      const data = await response.json();
      onLocationSelect?.(publicLocationsInsertSchema.parse(data.locationData));
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
    
    setShowDropdown(false);
    setPredictions([]);
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handleSelectPrediction(predictions[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowDropdown(false);
        break;
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (predictions.length > 0) {
            setShowDropdown(true);
          }
        }}
        className={className}
        {...props}
      />
      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className={cn(
            "absolute z-50 mt-1 w-full rounded-md border shadow-md bg-popover",
            "overflow-auto max-h-[300px]"
          )}
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.placeId}
              type="button"
              className={cn(
                "flex w-full flex-col px-3 py-2 text-left text-sm",
                "hover:bg-accent hover:text-accent-foreground",
                "focus:bg-accent focus:text-accent-foreground focus:outline-none",
                selectedIndex === index && "bg-accent text-accent-foreground"
              )}
              onClick={() => handleSelectPrediction(prediction)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="font-medium">
                {prediction.structuredFormat?.mainText.text ||
                  prediction.text.text.split(",")[0]}
              </span>
              {prediction.structuredFormat?.secondaryText && (
                <span className="text-xs text-muted-foreground">
                  {prediction.structuredFormat.secondaryText.text}
                </span>
              )}
              {prediction.distanceMeters && origin && (
                <span className="mt-1 text-xs text-muted-foreground">
                  {(prediction.distanceMeters / 1000).toFixed(1)} km away
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
